/**
 * Trainers / Coach utilities â€” queries coach_athlete_links, coaching_notes, trainer_profiles.
 */
import { supabase } from './supabase';

export type TrainerClientLink = {
  id: string;
  coach_user_id: string;
  athlete_user_id: string;
  status: string;
  created_at: string;
  /** Alias used by coach-dashboard */
  client_profile_id: string;
  /** Alias used by trainer profile page */
  trainer_profile_id?: string;
  /** Workouts per week target for this client — configurable per coach-client pair */
  adherence_target?: number; // workouts per week, default 3
};

export type TrainerLinkStatus = 'none' | 'pending' | 'active' | 'rejected';

/** List coach_athlete_links where the current user is the coach. */
export async function listMyClientLinks(): Promise<TrainerClientLink[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from('coach_athlete_links')
    .select('id, coach_user_id, athlete_user_id, status, created_at, adherence_target')
    .eq('coach_user_id', user.id);

  if (error) {
    console.warn('[trainers] listMyClientLinks error:', error.message);
    return [];
  }

  return (data ?? []).map((row: any) => ({
    ...row,
    client_profile_id: row.athlete_user_id,
  }));
}

/** Resolve a public profile by user id (lightweight). */
export async function resolveProfileById(userId: string) {
  const { data } = await supabase
    .from('profiles')
    .select('id, handle, display_name, avatar_url')
    .eq('id', userId)
    .maybeSingle();
  if (!data) return null;
  return { id: data.id, handle: data.handle, display_name: data.display_name, avatar_url: data.avatar_url, username: data.handle };
}

/** Get the link status between the current user and a given trainer profile. */
export async function getTrainerLinkStatus(trainerUserId: string): Promise<TrainerLinkStatus> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return 'none';

  const { data } = await supabase
    .from('coach_athlete_links')
    .select('status')
    .eq('coach_user_id', trainerUserId)
    .eq('athlete_user_id', user.id)
    .maybeSingle();

  if (!data) return 'none';
  return (data.status as TrainerLinkStatus) ?? 'none';
}

/**
 * Send a coach-athlete link request (athlete-initiated: athlete requests a coach).
 * The coach's personal coaching org is looked up automatically.
 */
export async function requestTrainerByProfileId(trainerUserId: string): Promise<{ ok: boolean; reason?: string }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { ok: false, reason: 'not_authenticated' };
  if (trainerUserId === user.id) return { ok: false, reason: 'cannot_request_self' };

  // Resolve coach's personal org — required for org_id NOT NULL constraint
  const { resolveCoachOwnerOrg } = await import('@/lib/orgProvisioning');
  const orgId = await resolveCoachOwnerOrg(trainerUserId);
  if (!orgId) return { ok: false, reason: 'coach_has_no_org' };

  const { error } = await supabase
    .from('coach_athlete_links')
    .insert({ org_id: orgId, coach_user_id: trainerUserId, athlete_user_id: user.id, status: 'pending' });

  return { ok: !error };
}

/** Get CTA config for trainer link status */
export function getCtaForStatus(status: TrainerLinkStatus, _isVerified: boolean): { label: string; variant: 'success' | 'secondary' | 'primary' | 'disabled'; disabled: boolean } {
  switch (status) {
    case 'active': return { label: 'Connected', variant: 'success', disabled: true };
    case 'pending': return { label: 'Pending', variant: 'secondary', disabled: true };
    case 'rejected': return { label: 'Request', variant: 'primary', disabled: false };
    default: return { label: 'Request', variant: 'primary', disabled: false };
  }
}

// â”€â”€ Types used by trainer pages â”€â”€

/**
 * Canonical trainer_profiles row type.
 * PK: trainer_profile_id → profiles(id)
 * Columns reconciled by migration 20260405000700_reconcile_trainer_profiles.sql.
 * Do NOT use: id (not a column), display_name (from profiles table), specialties (old alias).
 */
export type TrainerProfile = {
  trainer_profile_id: string;
  bio: string | null;
  verification_status: string;
  specializations: string[] | null;
  cert_url: string | null;
  cert_file_url: string | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string | null;
};

export type TrainerSearchResult = {
  trainer_profile_id: string;
  display_name: string | null;
  handle: string | null;
  username?: string | null;
  verification_status: string;
};

// â”€â”€ Additional exports used by trainer-dashboard & trainer profile pages â”€â”€

export async function getCurrentProfileId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id ?? null;
}

export async function getMyTrainerProfile(): Promise<TrainerProfile | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('trainer_profiles')
    .select('*')
    .eq('trainer_profile_id', user.id)
    .maybeSingle();
  return data as TrainerProfile | null;
}

export async function createTrainerProfile(profile?: Partial<TrainerProfile> | string): Promise<TrainerProfile | null> {
  // Accept string (cert_url) for backwards compat
  if (typeof profile === 'string') profile = { bio: profile } as Partial<TrainerProfile>;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  // Only pass columns that exist on the trainer_profiles table
  const payload: Record<string, unknown> = { trainer_profile_id: user.id };
  if (profile?.bio !== undefined) payload.bio = profile.bio;
  if (profile?.cert_url !== undefined) payload.cert_url = profile.cert_url;
  if (profile?.cert_file_url !== undefined) payload.cert_file_url = profile.cert_file_url;
  if (profile?.avatar_url !== undefined) payload.avatar_url = profile.avatar_url;
  if (profile?.specializations !== undefined) payload.specializations = profile.specializations;
  if (profile?.verification_status !== undefined) payload.verification_status = profile.verification_status;

  const { data, error } = await supabase
    .from('trainer_profiles')
    .upsert(payload)
    .select()
    .single();
  if (error) { console.warn('[trainers] createTrainerProfile error:', error.message); throw new Error(error.message); }
  return data as TrainerProfile;
}

/** Update an existing trainer profile. */
export async function updateTrainerProfile(fields: Partial<TrainerProfile>): Promise<TrainerProfile | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (fields.bio !== undefined) payload.bio = fields.bio;
  if (fields.cert_url !== undefined) payload.cert_url = fields.cert_url;
  if (fields.cert_file_url !== undefined) payload.cert_file_url = fields.cert_file_url;
  if (fields.avatar_url !== undefined) payload.avatar_url = fields.avatar_url;
  if (fields.specializations !== undefined) payload.specializations = fields.specializations;

  const { data, error } = await supabase
    .from('trainer_profiles')
    .update(payload)
    .eq('trainer_profile_id', user.id)
    .select()
    .single();
  if (error) { console.warn('[trainers] updateTrainerProfile error:', error.message); throw new Error(error.message); }
  return data as TrainerProfile;
}

export async function listPendingClientRequests(): Promise<TrainerClientLink[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data } = await supabase
    .from('coach_athlete_links')
    .select('id, coach_user_id, athlete_user_id, status, created_at, adherence_target')
    .eq('coach_user_id', user.id)
    .eq('status', 'pending');
  return (data ?? []).map((row: any) => ({ ...row, client_profile_id: row.athlete_user_id }));
}

export async function acceptClientRequest(linkId: string, _clientProfileId?: string): Promise<{ ok: boolean }> {
  const { error } = await supabase
    .from('coach_athlete_links')
    .update({ status: 'active' })
    .eq('id', linkId);
  return { ok: !error };
}

export async function rejectClientRequest(linkId: string, _clientProfileId?: string): Promise<{ ok: boolean }> {
  const { error } = await supabase
    .from('coach_athlete_links')
    .update({ status: 'rejected' })
    .eq('id', linkId);
  return { ok: !error };
}

/**
 * Request a coach-athlete link by username (handle lookup).
 *
 * @param username  The athlete's handle (without @)
 * @param orgId     The coach's personal coaching org ID (required — coach_athlete_links.org_id NOT NULL).
 *                  Pass the org_id from provisionCoachOrg / resolveCoachOwnerOrg.
 *                  If omitted, attempts a fallback lookup of the coach's owned coaching org.
 */
export async function requestTrainerClientLinkByUsername(
  username: string,
  orgId?: string,
): Promise<{ ok: boolean; reason?: string }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { ok: false, reason: 'not_authenticated' };

  // Resolve org_id: use provided value or look up the coach's personal coaching org
  let resolvedOrgId = orgId ?? null;
  if (!resolvedOrgId) {
    // Lazy import to avoid circular dependency
    const { resolveCoachOwnerOrg } = await import('@/lib/orgProvisioning');
    resolvedOrgId = await resolveCoachOwnerOrg(user.id);
  }
  if (!resolvedOrgId) {
    return { ok: false, reason: 'no_coach_org' };
  }

  // Find athlete by handle
  const { data: target } = await supabase
    .from('profiles')
    .select('id')
    .eq('handle', username)
    .maybeSingle();
  if (!target) return { ok: false, reason: 'user_not_found' };
  if (target.id === user.id) return { ok: false, reason: 'cannot_request_self' };

  const { error } = await supabase
    .from('coach_athlete_links')
    .insert({
      org_id: resolvedOrgId,
      coach_user_id: user.id,
      athlete_user_id: target.id,
      status: 'pending',
    });

  if (error) {
    console.warn('[trainers] requestTrainerClientLinkByUsername error:', error.message);
    return { ok: false, reason: error.message };
  }
  return { ok: true };
}

export async function listIncomingTrainerLinkRequests(): Promise<TrainerClientLink[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data } = await supabase
    .from('coach_athlete_links')
    .select('id, coach_user_id, athlete_user_id, status, created_at, adherence_target')
    .eq('athlete_user_id', user.id)
    .eq('status', 'pending');
  return (data ?? []).map((row: any) => ({ ...row, client_profile_id: row.athlete_user_id, trainer_profile_id: row.coach_user_id }));
}

export async function acceptTrainerLinkRequest(linkId: string): Promise<{ ok: boolean }> {
  return acceptClientRequest(linkId);
}

export async function rejectTrainerLinkRequest(linkId: string): Promise<{ ok: boolean }> {
  return rejectClientRequest(linkId);
}

/**
 * Returns the first active trainer link for the current athlete.
 * NOTE: Multi-coach is supported — an athlete may have multiple active links.
 * This helper returns the most recent one for backwards-compat display purposes.
 * Use listMyActiveTrainerLinks() to get all active coaches.
 */
export async function getMyActiveTrainerLink(): Promise<TrainerClientLink | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('coach_athlete_links')
    .select('id, coach_user_id, athlete_user_id, status, created_at, adherence_target')
    .eq('athlete_user_id', user.id)
    .eq('status', 'active')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (!data) return null;
  return { ...data, client_profile_id: data.athlete_user_id, trainer_profile_id: data.coach_user_id } as TrainerClientLink & { trainer_profile_id: string };
}

/** List all active trainer links for the current athlete (multi-coach support). */
export async function listMyActiveTrainerLinks(): Promise<TrainerClientLink[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data } = await supabase
    .from('coach_athlete_links')
    .select('id, coach_user_id, athlete_user_id, status, created_at, adherence_target')
    .eq('athlete_user_id', user.id)
    .eq('status', 'active')
    .order('created_at', { ascending: false });
  return (data ?? []).map((row: any) => ({
    ...row,
    client_profile_id: row.athlete_user_id,
    trainer_profile_id: row.coach_user_id,
  }));
}

export async function searchVerifiedTrainers(query: string): Promise<TrainerSearchResult[]> {
  const { data } = await supabase
    .from('trainer_profiles')
    .select('trainer_profile_id, verification_status')
    .eq('verification_status', 'verified')
    .limit(20);

  if (!data || data.length === 0) return [];
  const ids = data.map((d: any) => d.trainer_profile_id);
  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, handle, display_name')
    .in('id', ids)
    .or(`handle.ilike.%${query}%,display_name.ilike.%${query}%`);

  return (profiles ?? []).map((p: any) => ({
    trainer_profile_id: p.id,
    display_name: p.display_name,
    handle: p.handle,
    username: p.handle,
    verification_status: 'verified',
  }));
}

/**
 * Generate a shareable link for a coach's profile.
 * When opened, takes the user to the coach's profile where they can request coaching.
 */
export function generateCoachInviteLink(coachHandle: string): string {
  return `https://douevenlift.com/trainer/${encodeURIComponent(coachHandle)}?utm_source=duel&utm_medium=coach_invite&utm_campaign=coach_referral`;
}

/**
 * Get the coach's shareable invite code (their handle, or first 8 chars of user ID).
 * Coaches can text this to clients who then search/navigate to them.
 */
export async function getOrCreateCoachInviteCode(coachUserId: string): Promise<string> {
  const { data: profile } = await supabase
    .from('profiles')
    .select('handle')
    .eq('id', coachUserId)
    .maybeSingle();

  // Use handle as the invite identifier (clean, memorable)
  return profile?.handle ?? coachUserId.slice(0, 8);
}

/** Set a per-client adherence target (workouts per week). */
export async function setClientAdherenceTarget(
  linkId: string,
  target: number,
): Promise<boolean> {
  const { error } = await supabase
    .from('coach_athlete_links')
    .update({ adherence_target: target })
    .eq('id', linkId);

  if (error) {
    console.warn('[trainers] setClientAdherenceTarget failed:', error.message);
    return false;
  }
  return true;
}

/**
 * Notify clients when their coach's subscription lapses.
 * Doesn't delete links — just marks them as paused and notifies athletes.
 * Coach may re-subscribe to resume; athlete data is preserved.
 */
export async function handleCoachSubscriptionLapse(coachUserId: string): Promise<void> {
  const { data: activeLinks } = await supabase
    .from('coach_athlete_links')
    .select('id, athlete_user_id')
    .eq('coach_user_id', coachUserId)
    .eq('status', 'active');

  if (!activeLinks || activeLinks.length === 0) return;

  // Mark links as paused (not deleted — coach may re-subscribe)
  await supabase
    .from('coach_athlete_links')
    .update({ status: 'paused' })
    .eq('coach_user_id', coachUserId)
    .eq('status', 'active');

  // Notify each client (non-blocking)
  for (const link of activeLinks) {
    try {
      await supabase.from('notifications').insert({
        user_id: link.athlete_user_id,
        type: 'coach_paused',
        title: 'Coaching paused',
        body: "Your coach's subscription has been paused. Your workout data is safe — coaching features will resume when they re-subscribe.",
        data: { type: 'coach_paused', coachId: coachUserId },
        is_read: false,
      });
    } catch { /* non-blocking */ }
  }
}



